<template>
	<div class="app-tooltip">
		<div
			class="app-tooltip__activator"
			ref="activatorRef"
			@mouseenter="!props.showByActivatorClick ? showTooltip() : null"
			@mouseleave="!props.showByActivatorClick ? hideTooltip() : null"
		>
			<slot name="activator" />
		</div>

		<Teleport to="body">
			<div
				v-if="isVisible"
				ref="tooltipRef"
				class="app-tooltip__tooltip"
				:class="[
					`app-tooltip__tooltip--${computedPosition}`,
					{ 'app-tooltip__tooltip--with-arrow': showArrow }
				]"
				:style="tooltipStyles"
			>
				<div class="app-tooltip__tooltip-content" v-html="text" />
				<div
					v-if="showArrow"
					class="app-tooltip__tooltip-arrow"
					:class="`app-tooltip__tooltip-arrow--${computedPosition}`"
					:style="arrowStyles"
				/>
			</div>
		</Teleport>
	</div>
</template>

<script setup lang="ts">
import {
	ref,
	computed,
	watch,
	onMounted,
	onUnmounted,
	nextTick,
	type StyleValue
} from 'vue'
import { useBreakpoints } from '@/composables/useBreakpoints'

type AppTooltipProps = {
	position?: 'top' | 'bottom' | 'left' | 'right'
	showArrow?: boolean
	text: string
	maxWidth?: string
	showByActivatorClick?: boolean,
	closeByClickDelay?: number,
	delay?: number
}

const props = withDefaults(defineProps<AppTooltipProps>(), {
	position: 'top',
	showArrow: false,
	maxWidth: '300px',
	showByActivatorClick: false,
	closeByClickDelay: 3000,
	delay: 1000
})

const { isMobile, isTablet } = useBreakpoints()

const isVisible = ref(false)
const tooltipRef = ref<HTMLElement | null>(null)
const activatorRef = ref<HTMLElement | null>(null)

const computedPosition = ref(props.position)

const tooltipPosition = ref({ top: 0, left: 0 })
const arrowOffset = ref(0)
const delayTimer = ref<ReturnType<typeof setTimeout> | null>(null)

const showTooltip = () => {
	if (isMobile.value || isTablet.value) return
	if (delayTimer.value) clearTimeout(delayTimer.value)

	delayTimer.value = setTimeout(async () => {
		isVisible.value = true
		await nextTick()
		updatePosition()
		delayTimer.value = null
	}, props.delay)
}

const hideTooltip = () => {
	if (delayTimer.value) {
		clearTimeout(delayTimer.value)
		delayTimer.value = null
	}

	isVisible.value = false
}

const showByClick = async () => {
	if (isMobile.value || isTablet.value) return
	isVisible.value = true
	await nextTick()
	updatePosition()
	setTimeout(() => hideTooltip(), props.closeByClickDelay)
}

// Проверить, помещается ли тултип с указанной стороны
const checkPositionFit = (
	position: 'top' | 'bottom' | 'left' | 'right',
	top: number,
	left: number,
	tooltipRect: DOMRect,
	activatorRect: DOMRect
): boolean => {
	const viewportWidth = window.innerWidth
	const viewportHeight = window.innerHeight
	const minMargin = 10 // минимальный отступ от края экрана

	switch (position) {
		case 'top':
			return top >= minMargin
		case 'bottom':
			return top + tooltipRect.height <= viewportHeight - minMargin
		case 'left':
			return left >= minMargin
		case 'right':
			return left + tooltipRect.width <= viewportWidth - minMargin
		default:
			return true
	}
}

// Получить противоположную позицию
const getOppositePosition = (position: 'top' | 'bottom' | 'left' | 'right'): 'top' | 'bottom' | 'left' | 'right' => {
	switch (position) {
		case 'top': return 'bottom'
		case 'bottom': return 'top'
		case 'left': return 'right'
		case 'right': return 'left'
	}
}

// Обновить позицию тултипа
const updatePosition = () => {
	if (!activatorRef.value || !tooltipRef.value) return

	const activatorRect = activatorRef.value.getBoundingClientRect()
	const tooltipRect = tooltipRef.value.getBoundingClientRect()
	const scrollY = window.pageYOffset || document.documentElement.scrollTop
	const scrollX = window.pageXOffset || document.documentElement.scrollLeft

	// Пытаемся разместить тултип в запрошенной позиции
	let position = props.position
	let top = 0
	let left = 0
	const spacing = 8 // расстояние между активатором и тултипом

	// Рассчитываем идеальную позицию (по центру)
	switch (position) {
		case 'top':
			top = activatorRect.top + scrollY - tooltipRect.height - spacing
			left = activatorRect.left + scrollX + activatorRect.width / 2 - tooltipRect.width / 2
			break
		case 'bottom':
			top = activatorRect.bottom + scrollY + spacing
			left = activatorRect.left + scrollX + activatorRect.width / 2 - tooltipRect.width / 2
			break
		case 'left':
			top = activatorRect.top + scrollY + activatorRect.height / 2 - tooltipRect.height / 2
			left = activatorRect.left + scrollX - tooltipRect.width - spacing
			break
		case 'right':
			top = activatorRect.top + scrollY + activatorRect.height / 2 - tooltipRect.height / 2
			left = activatorRect.right + scrollX + spacing
			break
	}

	// Проверяем, помещается ли тултип в этой позиции
	const fitsOriginalPosition = checkPositionFit(
		position,
		top,
		left,
		tooltipRect,
		activatorRect
	)

	// Если не помещается, используем противоположную позицию
	if (!fitsOriginalPosition) {
		position = getOppositePosition(position)

		// Пересчитываем для противоположной позиции
		switch (position) {
			case 'top':
				top = activatorRect.top + scrollY - tooltipRect.height - spacing
				left = activatorRect.left + scrollX + activatorRect.width / 2 - tooltipRect.width / 2
				break
			case 'bottom':
				top = activatorRect.bottom + scrollY + spacing
				left = activatorRect.left + scrollX + activatorRect.width / 2 - tooltipRect.width / 2
				break
			case 'left':
				top = activatorRect.top + scrollY + activatorRect.height / 2 - tooltipRect.height / 2
				left = activatorRect.left + scrollX - tooltipRect.width - spacing
				break
			case 'right':
				top = activatorRect.top + scrollY + activatorRect.height / 2 - tooltipRect.height / 2
				left = activatorRect.right + scrollX + spacing
				break
		}
	}

	// Обновляем вычисленную позицию для отображения правильной стрелки
	computedPosition.value = position

	// Корректировка для предотвращения выхода за границы экрана
	const viewportWidth = window.innerWidth
	const viewportHeight = window.innerHeight
	const minMargin = 10

	// Сохраняем идеальную позицию для расчета смещения стрелки
	const idealLeft = left
	const idealTop = top

	// Корректировка по горизонтали
	if (left < minMargin) left = minMargin
	if (left + tooltipRect.width > viewportWidth - minMargin) {
		left = viewportWidth - tooltipRect.width - minMargin
	}

	// Корректировка по вертикали (если все равно не помещается даже в противоположной позиции)
	if (top < minMargin) top = minMargin
	if (top + tooltipRect.height > viewportHeight - minMargin) {
		top = viewportHeight - tooltipRect.height - minMargin
	}

	// Рассчитываем смещение стрелки
	arrowOffset.value = 0

	if (computedPosition.value === 'top' || computedPosition.value === 'bottom') {
		// Вычисляем центр активатора относительно тултипа
		const activatorCenterX = activatorRect.left + scrollX + activatorRect.width / 2
		const tooltipCenterX = left + tooltipRect.width / 2

		// Идеальное положение стрелки - по центру тултипа (50%)
		// Но если тултип смещен, нужно сместить стрелку относительно центра
		const idealArrowPosition = 50 // 50% от ширины тултипа

		// Вычисляем, насколько сместился тултип относительно идеальной позиции
		const tooltipShiftX = left - idealLeft

		// Вычисляем новую позицию стрелки
		let arrowPosition = idealArrowPosition - (tooltipShiftX / tooltipRect.width * 100)

		// Ограничиваем позицию стрелки, чтобы она не выходила за пределы тултипа
		const minArrowPosition = 15 // минимальный отступ от края тултипа
		const maxArrowPosition = 85 // максимальный отступ от края тултипа

		arrowPosition = Math.max(minArrowPosition, Math.min(maxArrowPosition, arrowPosition))

		arrowOffset.value = arrowPosition - 50 // сохраняем смещение от центра

	} else if (computedPosition.value === 'left' || computedPosition.value === 'right') {
		const activatorCenterY = activatorRect.top + scrollY + activatorRect.height / 2
		const tooltipCenterY = top + tooltipRect.height / 2

		const idealArrowPosition = 50 // 50% от высоты тултипа
		const tooltipShiftY = top - idealTop

		let arrowPosition = idealArrowPosition - (tooltipShiftY / tooltipRect.height * 100)

		const minArrowPosition = 15
		const maxArrowPosition = 85

		arrowPosition = Math.max(minArrowPosition, Math.min(maxArrowPosition, arrowPosition))

		arrowOffset.value = arrowPosition - 50
	}

	tooltipPosition.value = { top, left }
}

const tooltipStyles = computed<StyleValue>(() => ({
	'max-width': props.maxWidth,
	top: `${tooltipPosition.value.top}px`,
	left: `${tooltipPosition.value.left}px`,
}))

const arrowStyles = computed<StyleValue>(() => {
	const styles: any = {}

	if (computedPosition.value === 'top' || computedPosition.value === 'bottom') {
		styles.left = `calc(50% + ${arrowOffset.value}%)`
	} else if (computedPosition.value === 'left' || computedPosition.value === 'right') {
		styles.top = `calc(50% + ${arrowOffset.value}%)`
	}

	return styles
})

const handleResize = () => {
	if (isVisible.value) {
		updatePosition()
	}
}

const handleScroll = () => {
	if (isVisible.value) {
		updatePosition()
	}
}

watch(isVisible, (newValue) => {
	if (newValue) {
		window.addEventListener('resize', handleResize)
		window.addEventListener('scroll', handleScroll, true)
		updatePosition()
	} else {
		window.removeEventListener('resize', handleResize)
		window.removeEventListener('scroll', handleScroll, true)
	}
})

onUnmounted(() => {
	if (delayTimer.value) clearTimeout(delayTimer.value)

	window.removeEventListener('resize', handleResize)
	window.removeEventListener('scroll', handleScroll, true)
})

onMounted(() => {
	if (isVisible.value) {
		updatePosition()
	}
})

defineExpose({ showByClick })
</script>

<style scoped lang="scss">
.app-tooltip {
	display: inline-block;
	position: relative;

	&__activator {
		display: inline-block;
	}

	&__tooltip {
		position: fixed;
		z-index: 10003;
		padding: 4px 8px;
		background-color: #5C5C5CE5;
		color: white;
		border-radius: 8px;
		text-align: center;
		word-wrap: break-word;
		box-shadow: 0 1px 4px 0 #00000033;
		pointer-events: none;
		animation: tooltipFadeIn 0.2s ease-out;

		&-content {
			white-space: pre-wrap;
			overflow-wrap: break-word;
			font-size: 12px;
			line-height: 16px;
		}

		&-arrow {
			position: absolute;
			width: 0;
			height: 0;
			border-style: solid;
			pointer-events: none;

			&--top {
				top: 100%;
				left: 50%;
				transform: translateX(-50%);
				border-width: 8px 6px 0 6px;
				border-color: #5C5C5CE5 transparent transparent transparent;
			}

			&--bottom {
				bottom: 100%;
				left: 50%;
				transform: translateX(-50%);
				border-width: 0 6px 8px 6px;
				border-color: transparent transparent #5C5C5CE5 transparent;
			}

			&--left {
				left: 100%;
				top: 50%;
				transform: translateY(-50%);
				border-width: 6px 0 6px 8px;
				border-color: transparent transparent transparent #5C5C5CE5;
			}

			&--right {
				right: 100%;
				top: 50%;
				transform: translateY(-50%);
				border-width: 6px 8px 6px 0;
				border-color: transparent #5C5C5CE5 transparent transparent;
			}
		}
	}
}

@keyframes tooltipFadeIn {
	from {
		opacity: 0;
		transform: translateY(-5px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}
</style>
